import ConflictCard from './conflictCard';
import { useTranslation } from 'react-i18next';
import { usePage } from '../../context/PageContext';
import { useEffect, useState } from 'react';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { useFetchConflictSearches } from '../../hooks/useFetchConflictSearches';
import { useInView } from 'react-intersection-observer';
import { Spin } from '../Ant';
import { SearchEmpty } from '../icons';
import NoData from '../dashboard/noData';
import { IConflictSearch } from '../../interface/conflictSearch';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsPage } from '../../interface/analytics';
import { ROUTES } from '../../constants/constants';
import { useNavigationStack } from '../../context/NavigationStackContext';
import SkeletonCardList from '../common/skeleton/skeletonCardList';
import { useFetchUserParameters } from '../../hooks/useFetchUserParameters';
import { ParameterEntityType } from '../../interface/parameters';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';

const ConflictSearchListing = () => {
  const { t } = useTranslation();
  const { setTitle } = usePage();
  const { trackPage } = useAnalytics();
  const { push, clear } = useNavigationStack();
  const [searchValue, setSearchValue] = useState<string>('');
  const { data: parameters } = useFetchUserParameters({
    parametersToGet: ParameterEntityType.FEATURES_IN_USE,
  });

  const matterRequestInUse = parameters?.parameters?.featuresInUseParameters?.matterRequestInUse;
  const opportunityInUse = parameters?.parameters?.featuresInUseParameters?.opportunityInUse;
  const blackListInUse = parameters?.parameters?.featuresInUseParameters?.blackListInUse;
  const {
    data: conflictSearchesList,
    isLoading: isConflictSearchLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchConflictSearches({
    limit: 10,
    search: searchValue,
    relations: ['parentCompany', 'privatePersonCompany'],
    fields: [
      'name',
      'type',
      'homeCountry',
      'hasCoPartyRole',
      'hasOppositionRole',
      'hasOtherRole',
      'hasMatterRequests',
      'isBlackListed',
      'hasActiveOpportunities',
      'hasActiveConflictChecks',
    ],
  });
  useEffect(() => {
    trackPage(AnalyticsPage.CONFLICT_SEARCH_LISTING);
  }, [trackPage]);

  const handleHome = () => {
    clear();
    push(ROUTES.DASHBOARD);
  };
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        title={t('conflict:conflictSearch')}
        handleHome={handleHome}
        showMenu
        showSettings
      />,
    );
  }, []);

  const { ref, inView } = useInView({
    threshold: 0.5, // Trigger when 50% of the target is visible
    initialInView: false,
  });
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && conflictSearchesList?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" />;
    }
    return null;
  };
  const hasSearched = searchValue.length > 0;
  const showNoData =
    !conflictSearchesList ||
    (Object.entries(conflictSearchesList).length === 0 && !isConflictSearchLoading);
  return (
    <CollapsibleListLayout
      testId="conflict-search-listing"
      header={
        <SearchInputWithFilters
          searchTitle={t('conflict:conflictSearch')}
          autoFocus
          handleSearch={(val: string) => {
            setSearchValue(val);
          }}
        />
      }
    >
      {conflictSearchesList.map((val: IConflictSearch) => {
        return (
          <ConflictCard
            key={val.id}
            conflictSearch={val}
            matterRequestInUse={matterRequestInUse}
            opportunityInUse={opportunityInUse}
            blackListInUse={blackListInUse}
          />
        );
      })}
      {hasNextPage && <div ref={ref} className="loading-indicator" />}
      {showNoData && (
        <div className="flex-grow d-flex justify-center items-center">
          <NoData
            header={hasSearched ? t('conflict:noConflicts') : t('conflict:conflictSearch')}
            subHeader={
              hasSearched
                ? t('common:tryChangingSearchCriteria')
                : t('conflict:useSearchToFindPossibleConflicts')
            }
            icon={<SearchEmpty />}
          />
        </div>
      )}
      {isConflictSearchLoading ? (
        <SkeletonCardList rows={[1, 1, 2, 2]} withSubcontentOnFirstRow />
      ) : (
        renderLoader()
      )}
    </CollapsibleListLayout>
  );
};

export default ConflictSearchListing;
